import React, { useState, useEffect } from 'react';
import { ChevronDown, Droplets, AlertTriangle, Palette, Scan } from 'lucide-react';
import { supabase } from '../supabase';

/**
 * SkinAnalysisCardClassic — Prototype A, design v2 (2026-06-04 evening).
 *
 * Rebuild based on Sofia's reference screenshot:
 *   - Header: "Skin Analysis" + "powered by Son:mi"   (typography only, no emoji)
 *   - Big face photo, full card width
 *   - Three collapsed cards by default. Each has:
 *       leading visual  ·  KICKER LABEL  ·  bold value  ·  optional pill  ·  caret
 *     Tap to expand, divider rule appears, detail shows below.
 *   - Detected Concerns is a single card containing multiple expandable rows
 *     (one per concern). Each row has a coloured severity dot, name, severity
 *     pill (LOW/MEDIUM/HIGH), caret.
 *   - Single bottom CTA: navy filled card "Personalized Recommendations" with
 *     sub-line "{N} suggestions". Tap to expand the recs inline.
 *   - Disclaimer in soft amber callout at bottom.
 *
 * No emoji characters anywhere — only lucide-react SVGs (Droplets, Sparkles
 * etc.) which are vector icons, not Unicode emoji. The leading "skin-tone
 * swatch" is a calculated CSS colour block, also not an emoji.
 *
 * Parsing logic preserved from earlier deployed card — same regexes, same
 * downstream consumers (ChatComponent skin-journal mirror).
 */
const SkinAnalysisCardClassic = ({
  analysis,
  imageUrl,
  userId,
  onAddRoutine,
  isAnonymous = false,
  onShowSignIn,
  onAnalyseProduct,
  onSeeShadeMatches,
  isScan = false,
  photoCount = 0,
  angleImages = null,
  insight = '',
  onReadFeedback,
  onCorrectionSubmit,
}) => {
  // ── accuracy check ─────────────────────────────────────────────────────────
  // "Does this sound like your skin?" is an ACCURACY question, not satisfaction.
  // Which field the user corrects is the per-field quality signal for the vision
  // model, and the correction improves their profile immediately.
  const [feedback, setFeedback] = useState(null);   // null | 'spot_on' | 'not_quite'
  const [correctField, setCorrectField] = useState('');
  const [correctValue, setCorrectValue] = useState(''); // structured corrected value (skin type)
  const [correctNote, setCorrectNote] = useState('');
  const [corrected, setCorrected] = useState(false);

  // ── hasRoutine lookup ───────────────────────────────────────────────────────
  const [hasRoutine, setHasRoutine] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let uid = userId;
        if (!uid) {
          const { data: { user } } = await supabase.auth.getUser();
          uid = user?.id;
        }
        if (!uid) { if (!cancelled) setHasRoutine(false); return; }
        const { data } = await supabase.from('skin_routine_data').select('routines').eq('user_id', uid).single();
        const routines = Array.isArray(data?.routines) ? data.routines : [];
        const hasProduct = routines.some(r =>
          [...(r?.morningSteps || []), ...(r?.eveningSteps || []), ...(r?.steps || [])]
            .some(s => s && (s.productName || '').trim())
        );
        if (!cancelled) setHasRoutine(hasProduct);
      } catch {
        if (!cancelled) setHasRoutine(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // ── parsers (unchanged from earlier) ────────────────────────────────────────
  const parseSections = (text) => {
    const sections = {
      skinTone: '', skinType: '', confidence: '', concerns: '', recommendations: '', disclaimer: '',
    };
    String(text || '').split(/## /g).filter(Boolean).forEach(part => {
      const lines = part.trim().split('\n');
      const header = lines[0].toLowerCase();
      const content = lines.slice(1).join('\n').trim();
      if (header.includes('skin tone')) sections.skinTone = content;
      else if (header.includes('skin type')) sections.skinType = content;
      else if (header.includes('confidence')) sections.confidence = content;
      else if (header.includes('concern')) sections.concerns = content;
      else if (header.includes('recommendation')) sections.recommendations = content;
      else if (header.includes('disclaimer')) sections.disclaimer = content;
    });
    return sections;
  };
  const stripMarkdown = (text) => !text ? text : text.replace(/\*\*/g, '').replace(/^\*(.+)\*$/s, '$1');
  const extractToneInfo = (text) => {
    const toneMatch = text.match(/(very[- ]deep|light[- ]medium|medium[- ]deep|fair|light|medium|tan|deep|rich)/i);
    const undertoneMatch = text.match(/(warm|cool|neutral|olive)/i);
    const titleCase = (str) => !str ? '' : str.trim().replace(/\s+/g, '-').toLowerCase().split('-').map(w => w[0].toUpperCase() + w.slice(1)).join('-');
    return {
      tone: toneMatch ? titleCase(toneMatch[1]) : null,
      undertone: undertoneMatch ? undertoneMatch[1][0].toUpperCase() + undertoneMatch[1].slice(1).toLowerCase() : null,
      description: stripMarkdown(text),
    };
  };
  const extractSkinType = (text) => {
    const typeMatch = text.match(/(oily|dry|combination|normal|sensitive)/i);
    const confidenceMatch = sections.confidence.match(/(\d+)%/);
    const characteristics = [];
    text.split('\n').forEach(line => {
      const t = line.trim();
      if (!t || t.toLowerCase().includes('characteristics') || t.toLowerCase().includes('visible') || t.toLowerCase().includes('then list')) return;
      if (t.startsWith('-') || t.startsWith('*') || t.startsWith('•')) {
        characteristics.push(stripMarkdown(t.replace(/^[-*•]\s*/, '').trim()));
      }
    });
    return {
      type: typeMatch ? typeMatch[1][0].toUpperCase() + typeMatch[1].slice(1) : null,
      confidence: confidenceMatch ? parseInt(confidenceMatch[1]) : null,
      characteristics,
    };
  };
  const parseConcerns = (text) => {
    if (text.toLowerCase().match(/(no concerns detected|no major concerns|none detected)/)) return [];
    const concerns = [];
    text.split('\n').forEach(line => {
      line = line.trim();
      if (!line || !line.startsWith('-') || line.toLowerCase().includes('examples:') || line.toLowerCase().includes('if none are visible')) return;
      const fmt1 = line.match(/^[-*•]\s*\*\*([^*]+)\*\*\s*\(Severity:\s*(LOW|MEDIUM|HIGH),\s*Confidence:\s*(\d+)%\):\s*(.+)/i);
      const fmt2 = !fmt1 && line.match(/^[-*•]\s*\*\*([^(]+?)\s*\(Severity:\s*(LOW|MEDIUM|HIGH),\s*Confidence:\s*(\d+)%\)\*\*\s*[:\-]?\s*(.+)/i);
      const m = fmt1 || fmt2;
      if (m) concerns.push({ title: m[1].trim(), severity: m[2].toLowerCase(), confidence: parseInt(m[3]), description: m[4].trim() });
      else {
        const cleaned = line.replace(/\(Severity:.*?%\)\**/gi, '').replace(/\*\*/g, '').trim();
        const sm = cleaned.match(/^[-*•]\s*(.+?):\s*(.+)/);
        if (sm) concerns.push({ title: sm[1].trim(), severity: 'low', confidence: null, description: sm[2].trim() });
      }
    });
    return concerns;
  };
  const parseRecommendations = (text) => {
    // Light parsing of the recommendations section into a flat list of strings
    // for the Personalized Recommendations expander. Keeps it simple — one
    // bullet per line, header lines dropped.
    const items = [];
    text.split('\n').forEach(line => {
      const t = line.trim();
      if (!t) return;
      if (t.startsWith('-') || t.startsWith('*') || t.startsWith('•')) {
        const cleaned = stripMarkdown(t.replace(/^[-*•]\s*/, '').trim());
        if (cleaned) items.push(cleaned);
      }
    });
    return items;
  };

  const sections = parseSections(analysis);
  const toneInfo = extractToneInfo(sections.skinTone);
  const skinTypeInfo = extractSkinType(sections.skinType);
  const concerns = parseConcerns(sections.concerns);
  const recommendations = parseRecommendations(sections.recommendations);

  // Bucket a 0-100 confidence integer into a qualitative label. Per Sofia
  // (2026-06-04): "82%" everywhere reads as suspiciously uniform; a label
  // is more honest and easier to scan.
  //   > 60 → High,  40–60 → Medium,  < 40 → Low.
  const confidenceWord = (c) => {
    if (c == null) return null;
    if (c > 60) return 'High';
    if (c >= 40) return 'Medium';
    return 'Low';
  };

  const handleAddRoutine = () => {
    if (isAnonymous && onShowSignIn) { onShowSignIn(); return; }
    if (onAddRoutine) onAddRoutine();
  };

  // The Skin Tone section from the LLM has the shape:
  //   <one-line summary>      ← Sofia wants this as a standalone lead line,
  //   <multi-sentence detail    NOT a bullet
  //    paragraph>              ← these become sentence-level bullets
  //
  // (2026-06-04 polish: first bullet was previously the summary + the first
  // sentence of detail mashed together because the splitter didn't see the
  // newline as a sentence boundary.)
  const splitToneContent = (s) => {
    if (!s) return { lead: '', bullets: [] };
    const lines = s.trim().split(/\n+/).map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return { lead: '', bullets: [] };
    const lead = lines[0];
    const rest = lines.slice(1).join(' ').trim();
    const bullets = rest
      ? rest.split(/(?<=[.!?])\s+(?=[A-Z])/).map(t => t.trim()).filter(Boolean)
      : [];
    return { lead, bullets };
  };

  // Skin tone → hex swatch colour. Used in the leading visual on the Skin
  // Tone card. Approximate human skin-tone hexes; tuned for "feels right"
  // not for clinical accuracy.
  //
  // 2026-06-04: ordering bug fix. The previous version checked `deep` before
  // `medium-deep` and `medium` before `light-medium`, so compound buckets fell
  // through to the wrong (darker) colour — "Medium-Deep" rendered with the
  // "Deep" swatch. Fix: check more-specific compound names FIRST so they win
  // before the substring also matches the shorter bucket name.
  const toneSwatchHex = (() => {
    const t = (toneInfo.tone || '').toLowerCase();
    if (t.includes('very-deep') || t.includes('rich')) return '#4A2818';
    if (t.includes('medium-deep')) return '#8A5A30';
    if (t.includes('deep')) return '#6E4424';
    if (t.includes('tan')) return '#A26F47';
    if (t.includes('light-medium')) return '#DDB48A';
    if (t.includes('medium')) return '#C49870';
    if (t.includes('fair')) return '#F5E1C8';
    if (t.includes('light')) return '#E8C9A2';
    return '#D4B597';
  })();

  // Compose the inline "Medium · Warm" value for the Skin Tone collapsed
  // header. If either piece is missing, falls back gracefully.
  const skinToneValueLine = (() => {
    if (toneInfo.tone && toneInfo.undertone) {
      return (
        <span>
          {toneInfo.tone}
          <span className="text-[#2A324B]/40 mx-1.5">·</span>
          {toneInfo.undertone}
        </span>
      );
    }
    return <span>{toneInfo.tone || toneInfo.undertone || 'Analysed'}</span>;
  })();

  return (
    <div className="w-full space-y-4">
        {/* ── Header ── typography only, no outer card */}
        <div>
          <h2 className="text-xl font-bold text-[#2A324B] leading-tight">Skin Analysis</h2>
          <p className="text-xs text-[#2A324B]/60 mt-0.5">powered by Son:mi</p>
        </div>

        {/* ── Insight ── opens the card with something only a look at THIS face
            could reveal, so the read invites a question instead of reading as a
            spec sheet. Same analysis call; absent = nothing rendered. */}
        {insight && (
          <p className="text-[17px] leading-snug font-semibold text-[#2A1212]">
            {insight}
          </p>
        )}

        {/* ── Hero photo ── square crop with center-top object position so
            portrait selfies don't get their forehead/chin chopped (per Sofia
            2026-06-04 — was 16:9 which cropped awkwardly for selfies). */}
        {imageUrl && (
          <div>
            <div
              className="relative rounded-2xl overflow-hidden ring-1 ring-black/5 bg-[#F2EAE1]"
              style={{ aspectRatio: '1 / 1', minHeight: 1 }}
            >
              <img
                src={imageUrl}
                alt="Analysed face"
                loading="eager"
                decoding="async"
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  maxWidth: 'none',
                  objectFit: 'cover',
                  objectPosition: 'center top',
                }}
              />
              {/* Scan badge — signals this is a captured scan result, not a bare
                  selfie. When it's a multi-angle scan, name the photo count so it
                  doesn't read as "we just analysed one pic". */}
              {isScan && (
                <div className="absolute bottom-2.5 left-2.5 flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full bg-black/45 backdrop-blur-sm">
                  <Scan className="w-3 h-3 text-white" strokeWidth={2.25} />
                  <span className="text-[13px] font-semibold text-white tracking-wide">Skin scan</span>
                </div>
              )}
            </div>
            {/* Angle thumbnails — the other captured shots (cheeks, chin), so the
                card visibly reflects a multi-photo scan, not a single selfie. */}
            {Array.isArray(angleImages) && angleImages.length > 1 && (
              <div className="flex gap-1.5 mt-2">
                {angleImages.slice(0, 6).map((src, i) => (
                  <div key={i} className="w-11 h-11 rounded-lg overflow-hidden ring-1 ring-black/5 flex-shrink-0">
                    <img src={src} alt={`Scan angle ${i + 1}`} className="w-full h-full object-cover" style={{ objectPosition: 'center top' }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Section cards ── */}
        <div className="space-y-3">

          {/* Skin Tone */}
          <ExpandableSection
            leading={
              <span
                className="w-9 h-9 rounded-lg ring-1 ring-black/10 flex-shrink-0"
                style={{ backgroundColor: toneSwatchHex }}
                aria-hidden="true"
              />
            }
            kicker="Skin Tone"
            value={skinToneValueLine}
          >
            {(() => {
              const { lead, bullets } = splitToneContent(toneInfo.description);
              return (
                <>
                  {lead && (
                    <p className="text-sm text-[#2A324B]/90 leading-relaxed font-medium mb-3">
                      {lead}
                    </p>
                  )}
                  {bullets.length > 0 && (
                    <ul className="space-y-2">
                      {bullets.map((b, i) => (
                        <li key={i} className="text-sm text-[#2A324B]/75 flex items-start gap-2.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#F3D5B5] mt-2 flex-shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              );
            })()}
          </ExpandableSection>

          {/* Skin Type */}
          <ExpandableSection
            leading={
              <span className="w-9 h-9 rounded-lg bg-[#F3D5B5]/30 flex items-center justify-center flex-shrink-0">
                <Droplets className="w-4 h-4 text-[#2A324B]" />
              </span>
            }
            kicker="Skin Type"
            value={skinTypeInfo.type || 'Analysed'}
          >
            {skinTypeInfo.characteristics.length > 0 && (
              <ul className="space-y-2">
                {skinTypeInfo.characteristics.map((c, i) => (
                  <li key={i} className="text-sm text-[#2A324B]/80 flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F3D5B5] mt-2 flex-shrink-0" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            )}
            {/* Confidence shown as in-line caption inside the expansion, same
                pattern as Detected Concerns rows. "High" on its own next to
                "Normal" read as "high WHAT?" — this resolves the ambiguity. */}
            {confidenceWord(skinTypeInfo.confidence) && (
              <p className="text-[13px] text-[#2A324B]/60 mt-3">
                Detection confidence: <span className="font-semibold text-[#2A324B]/80">{confidenceWord(skinTypeInfo.confidence)}</span>
              </p>
            )}
          </ExpandableSection>

          {/* Detected Concerns — single card with multiple expandable rows */}
          {concerns.length > 0 && (
            <div className="bg-white rounded-2xl ring-1 ring-black/5 p-4">
              <p className="text-[13px] uppercase tracking-widest text-[#2A324B]/55 font-semibold mb-2">
                Detected Concerns
              </p>
              <div className="-mx-1">
                {concerns.map((c, i) => (
                  <ConcernRow key={i} concern={c} isLast={i === concerns.length - 1} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Accuracy check ── one tap. "Not quite" opens a structured
            correction rather than a dead-end "thanks for your feedback". */}
        {!corrected && (
          <div className="bg-[#F2EAE1] rounded-2xl p-4 ring-1 ring-[#341C1C]/10">
            {feedback !== 'not_quite' ? (
              <>
                <p className="text-sm font-semibold text-[#2A1212] mb-3">
                  {feedback === 'spot_on' ? 'Thanks — noted.' : 'Does this sound like your skin?'}
                </p>
                {feedback !== 'spot_on' && (
                  <div className="flex gap-2.5">
                    <button
                      type="button"
                      onClick={() => { setFeedback('spot_on'); onReadFeedback?.('spot_on'); }}
                      className="flex-1 bg-white rounded-full py-2.5 text-sm font-semibold text-[#2A324B] ring-1 ring-[#2A324B]/15 hover:ring-[#2A324B]/40 transition-all"
                    >
                      Spot on
                    </button>
                    <button
                      type="button"
                      onClick={() => { setFeedback('not_quite'); onReadFeedback?.('not_quite'); }}
                      className="flex-1 bg-white rounded-full py-2.5 text-sm font-semibold text-[#2A324B] ring-1 ring-[#2A324B]/15 hover:ring-[#2A324B]/40 transition-all"
                    >
                      Not quite
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Collaborative framing: refinement, not "the scan was wrong" —
                    scan + user beats either alone, and it keeps the read's
                    credibility while still getting the correction. */}
                <p className="text-sm font-semibold text-[#2A1212] mb-3">
                  Let's fine-tune this together. Which part feels off?
                </p>
                <p className="text-sm text-[#2A324B]/65 -mt-1 mb-3">
                  Choose the correction below, then tap Send so Son:mi remembers it.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Skin type', 'Skin tone & undertone', 'The concerns', 'Something else'].map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setCorrectField(f)}
                      className={`rounded-full px-3.5 py-2 text-[13px] transition-all ${
                        correctField === f
                          ? 'bg-[#F3D5B5]/50 ring-1 ring-[#F3D5B5] text-[#2A1212] font-semibold'
                          : 'bg-white ring-1 ring-[#2A324B]/15 text-[#2A324B] hover:ring-[#2A324B]/40'
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
                {correctField === 'Skin type' && (
                  <>
                    <p className="text-[13px] text-[#2A324B]/50 mt-3 mb-1.5">What is it, then?</p>
                    <div className="flex flex-wrap gap-2">
                      {['Dry', 'Oily', 'Combination', 'Sensitive', 'Normal'].map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setCorrectValue(v)}
                          className={`rounded-full px-3.5 py-2 text-[13px] transition-all ${
                            correctValue === v
                              ? 'bg-[#F3D5B5]/50 ring-1 ring-[#F3D5B5] text-[#2A1212] font-semibold'
                              : 'bg-white ring-1 ring-[#2A324B]/15 text-[#2A324B] hover:ring-[#2A324B]/40'
                          }`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                <p className="text-[13px] text-[#2A324B]/50 mt-3 mb-1.5">
                  Want to tell me more? <span className="font-normal">optional</span>
                </p>
                <textarea
                  rows={2}
                  value={correctNote}
                  onChange={(e) => setCorrectNote(e.target.value)}
                  placeholder="e.g. my T-zone gets oily but cheeks are dry"
                  className="w-full bg-white rounded-xl px-3.5 py-2.5 text-sm text-[#2A324B] ring-1 ring-[#2A324B]/15 focus:ring-[#2A324B]/40 outline-none resize-none placeholder:text-[#2A324B]/35"
                />
                <button
                  type="button"
                  disabled={!correctField || (correctField === 'Skin type' && !correctValue && !correctNote.trim())}
                  onClick={() => {
                    // Lead with the structured value (skin type) so the advisor gets the
                    // corrected ANSWER, not just "this field is off"; free text follows.
                    onCorrectionSubmit?.({
                      field: correctField,
                      note: [correctField === 'Skin type' ? correctValue : '', correctNote.trim()]
                        .filter(Boolean).join(' — '),
                    });
                    setCorrected(true);
                  }}
                  className="w-full mt-3 rounded-full py-3 text-sm font-semibold text-white bg-[#341C1C] disabled:opacity-40 transition-opacity"
                >
                  Send
                </button>
              </>
            )}
          </div>
        )}

        {/* ── What you can do next ── only the two things people actually do
            after a scan (ask: 54%, shade: 26%). Product analysis (~0%) and
            routine (3%) are deliberately not competing for attention here. */}
        <div className="pt-1 space-y-3">
          {onSeeShadeMatches && (
            <button
              type="button"
              onClick={onSeeShadeMatches}
              className="w-full flex items-center gap-3 bg-white rounded-2xl px-4 py-3.5 ring-1 ring-[#2A324B]/15 hover:ring-[#2A324B]/40 hover:bg-[#F2EAE1]/40 active:scale-[0.99] transition-all text-left"
            >
              <span className="w-9 h-9 rounded-xl bg-[#F3D5B5]/50 flex items-center justify-center text-[#2A324B] flex-shrink-0">
                <Palette className="w-5 h-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-[#2A324B]">See my shade matches</span>
                <span className="block text-[13px] text-[#2A324B]/60 mt-0.5">Foundations for your tone</span>
              </span>
            </button>
          )}
          {hasRoutine === false && (
            <button
              type="button"
              onClick={handleAddRoutine}
              className="w-full text-center text-xs text-[#2A324B]/60 hover:text-[#2A324B] underline underline-offset-4 decoration-[#2A324B]/30 pt-1"
            >
              Add your routine for fully tailored advice →
            </button>
          )}
        </div>

        {/* ── Disclaimer (lucide icon, no emoji) ── */}
        {sections.disclaimer && (
          <div className="bg-amber-50/60 border border-amber-200/60 rounded-2xl p-3 flex items-start gap-2.5">
            <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-900/85 leading-relaxed">
              {stripMarkdown(sections.disclaimer)}
            </p>
          </div>
        )}
    </div>
  );
};


// Module-scope helper so ConcernRow + the main component share one bucketing.
// Mirrors `confidenceWord` inside the main component (which closes over local
// state). Keeping both means each scope has its own clean reference, no
// prop-drilling.
const bucketize = (c) => {
  if (c == null) return null;
  if (c > 60) return 'High';
  if (c >= 40) return 'Medium';
  return 'Low';
};


// ── ExpandableSection ────────────────────────────────────────────────────────
// Reusable card row used by Skin Tone and Skin Type. Collapsed shows just the
// header line; expanded reveals children below a hairline divider.
const ExpandableSection = ({ leading, kicker, value, trailingPill, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl ring-1 ring-black/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-[#F2EAE1]/30 transition-colors"
        aria-expanded={open}
      >
        {leading}
        <div className="flex-1 min-w-0">
          <p className="text-[13px] uppercase tracking-widest text-[#2A324B]/55 font-semibold">
            {kicker}
          </p>
          <p className="text-base font-bold text-[#2A324B] leading-tight mt-0.5 truncate">
            {value}
          </p>
        </div>
        {trailingPill && (
          <span className="px-2.5 py-1 bg-[#F3D5B5]/40 text-[#2A324B] rounded-full text-xs font-semibold flex-shrink-0">
            {trailingPill}
          </span>
        )}
        <ChevronDown className={`w-4 h-4 text-[#2A324B]/40 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1">
          <div className="border-t border-[#2A324B]/8 pt-3">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};


// ── ConcernRow ───────────────────────────────────────────────────────────────
// One expandable row inside the Detected Concerns card. Severity colours
// follow your reference: amber for medium, green for low, red for high.
const ConcernRow = ({ concern, isLast }) => {
  const [open, setOpen] = useState(false);

  const severity = concern.severity || 'low';
  const sevColor =
    severity === 'high' ? 'bg-red-400' :
    severity === 'medium' ? 'bg-amber-400' :
    'bg-emerald-400';
  const sevPill =
    severity === 'high'
      ? 'bg-red-50 text-red-700 ring-red-200'
      : severity === 'medium'
      ? 'bg-amber-50 text-amber-700 ring-amber-200'
      : 'bg-emerald-50 text-emerald-700 ring-emerald-200';

  return (
    <div className={isLast ? '' : 'border-b border-[#2A324B]/8'}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-1 py-3 text-left"
        aria-expanded={open}
      >
        <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 self-start ${sevColor}`} />
        <span className="flex-1 min-w-0 text-sm font-semibold text-[#2A324B] break-words">
          {concern.title}
        </span>
        <span className={`px-2.5 py-1 rounded-full text-[13px] font-semibold capitalize ring-1 ${sevPill} flex-shrink-0`}>
          {severity}
        </span>
        <ChevronDown className={`w-4 h-4 text-[#2A324B]/40 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-1 pb-3 pt-1">
          <p className="text-xs text-[#2A324B]/75 leading-relaxed">{concern.description}</p>
          {concern.confidence != null && (
            <p className="text-[13px] text-[#2A324B]/60 mt-2">
              Detection confidence: <span className="font-semibold text-[#2A324B]/80">{bucketize(concern.confidence)}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
};


// ── CtaSquare ────────────────────────────────────────────────────────────────
// Two-CTA pattern at the bottom of the card. Both white with a peach-tinted
// icon block — same family, primary differentiates via slightly warmer
// icon-block tint (no more navy+beige combo Sofia called out).
const CtaSquare = ({ icon, label, sub, onClick, variant }) => {
  const primary = variant === 'primary';
  return (
    <button
      type="button"
      onClick={onClick}
      className="bg-white text-[#2A324B] rounded-2xl p-4 flex flex-col items-start gap-2.5 text-left ring-1 ring-[#2A324B]/15 hover:ring-[#2A324B]/40 hover:bg-[#F2EAE1]/40 active:scale-[0.98] transition-all shadow-sm min-h-[110px]"
    >
      <span className={
        primary
          ? 'w-9 h-9 rounded-xl bg-[#F3D5B5]/50 flex items-center justify-center text-[#2A324B]'
          : 'w-9 h-9 rounded-xl bg-[#F2EAE1] flex items-center justify-center text-[#2A324B]'
      }>
        {icon}
      </span>
      <div>
        <p className="text-sm font-semibold text-[#2A324B]">{label}</p>
        <p className="text-[13px] mt-0.5 text-[#2A324B]/60 leading-snug">{sub}</p>
      </div>
    </button>
  );
};

export default SkinAnalysisCardClassic;
